const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 8;
const DAYS_PER_WEEK = 5;

const TIME_SEGMENT_REGEX = /(\d+(?:\.\d+)?)\s*([wdhm])/gi;

export const formatMinutes = (minutes?: number | null): string => {
  if (!minutes || minutes <= 0) return '0m';

  const weeks = Math.floor(minutes / (DAYS_PER_WEEK * HOURS_PER_DAY * MINUTES_PER_HOUR));
  let remaining = minutes % (DAYS_PER_WEEK * HOURS_PER_DAY * MINUTES_PER_HOUR);

  const days = Math.floor(remaining / (HOURS_PER_DAY * MINUTES_PER_HOUR));
  remaining %= HOURS_PER_DAY * MINUTES_PER_HOUR;

  const hours = Math.floor(remaining / MINUTES_PER_HOUR);
  const mins = remaining % MINUTES_PER_HOUR;

  const parts: string[] = [];
  if (weeks) parts.push(`${weeks}w`);
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins) parts.push(`${mins}m`);

  return parts.join(' ') || '0m';
};

export const parseTimeExpression = (value: string): number => {
  if (!value) return 0;
  let totalMinutes = 0;

  const matches = value.matchAll(TIME_SEGMENT_REGEX);
  for (const match of matches) {
    const amount = parseFloat(match[1]);
    const unit = match[2];

    if (Number.isNaN(amount)) continue;

    switch (unit) {
      case 'w':
      case 'W':
        totalMinutes += amount * DAYS_PER_WEEK * HOURS_PER_DAY * MINUTES_PER_HOUR;
        break;
      case 'd':
      case 'D':
        totalMinutes += amount * HOURS_PER_DAY * MINUTES_PER_HOUR;
        break;
      case 'h':
      case 'H':
        totalMinutes += amount * MINUTES_PER_HOUR;
        break;
      case 'm':
      case 'M':
        totalMinutes += amount;
        break;
      default:
        break;
    }
  }

  return Math.round(totalMinutes);
};

export const isValidTimeExpression = (value: string): boolean => {
  if (!value) return false;
  const normalized = value.trim();
  if (!normalized) return false;
  const pattern = /^(\d+(\.\d+)?\s*[wdhm]\s*)+$/i;
  return pattern.test(normalized);
};

export const clampMinutes = (value: number, max: number = 999_999): number => {
  return Math.min(Math.max(Math.round(value), 0), max);
};

export const formatDateTimeShort = (value?: string): string => {
  if (!value) return '';
  
  // Parse the date - API returns UTC ISO strings
  // If date doesn't have timezone info, assume it's UTC
  let date: Date;
  
  if (typeof value === 'string') {
    const tzRegex = /([zZ]|[+-]\d{2}:\d{2})$/;
    if (tzRegex.test(value)) {
      date = new Date(value);
    } else if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      date = new Date(value + 'Z');
    } else if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      date = new Date(value + 'T00:00:00Z');
    } else {
      date = new Date(value);
    }
  } else {
    date = new Date(value);
  }
  
  if (isNaN(date.getTime())) {
    console.warn('Invalid date value:', value);
    return value;
  }
  
  // Format using local timezone - toLocaleString automatically converts from UTC to local time
  // This ensures UTC times from the server are displayed in the user's local timezone
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: undefined, // Use browser's local timezone
  };
  
  return date.toLocaleString('en-US', options);
};

